import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAutomationClient } from '../providers/AutomationProvider'
import type {
  TestSuitesListParams,
  TestSuiteRunsListParams,
  CreateTestSuiteParams,
  UpdateTestSuiteParams,
  UpdateTestSuiteScheduleParams,
  AddTestCasesToSuiteParams,
  ReorderTestCasesParams,
} from '../types'

/**
 * Query key factory for test-suites-related queries
 */
export const testSuitesKeys = {
  all: ['test-suites'] as const,
  lists: () => [...testSuitesKeys.all, 'list'] as const,
  list: (params?: TestSuitesListParams) => [...testSuitesKeys.lists(), params] as const,
  details: () => [...testSuitesKeys.all, 'detail'] as const,
  detail: (id: string) => [...testSuitesKeys.details(), id] as const,
  runs: (id: string) => [...testSuitesKeys.detail(id), 'runs'] as const,
  runsList: (id: string, params?: TestSuiteRunsListParams) => [...testSuitesKeys.runs(id), params] as const,
  run: (id: string, runId: string) => [...testSuitesKeys.runs(id), runId] as const,
  flaky: () => [...testSuitesKeys.all, 'flaky'] as const,
  coverage: () => [...testSuitesKeys.all, 'coverage'] as const,
}

/**
 * Hook to fetch a paginated list of test suites
 */
export function useTestSuites(params?: TestSuitesListParams) {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: testSuitesKeys.list(params),
    queryFn: () => client.testSuites.list(params),
  })
}

/**
 * Hook to fetch a single test suite with details
 */
export function useTestSuite(id: string, options?: { enabled?: boolean }) {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: testSuitesKeys.detail(id),
    queryFn: () => client.testSuites.get(id),
    enabled: options?.enabled !== false && !!id,
  })
}

/**
 * Hook to create a new test suite
 */
export function useCreateTestSuite() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateTestSuiteParams) => client.testSuites.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: testSuitesKeys.lists() })
    },
  })
}

/**
 * Hook to update a test suite
 */
export function useUpdateTestSuite() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTestSuiteParams }) =>
      client.testSuites.update(id, data),
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: testSuitesKeys.lists() })
      queryClient.setQueryData(testSuitesKeys.detail(id), data)
    },
  })
}

/**
 * Hook to delete a test suite
 */
export function useDeleteTestSuite() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => client.testSuites.delete(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: testSuitesKeys.lists() })
      queryClient.removeQueries({ queryKey: testSuitesKeys.detail(id) })
    },
  })
}

/**
 * Hook to update a test suite's schedule
 */
export function useUpdateTestSuiteSchedule() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTestSuiteScheduleParams }) =>
      client.testSuites.updateSchedule(id, data),
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: testSuitesKeys.lists() })
      queryClient.setQueryData(testSuitesKeys.detail(id), data)
    },
  })
}

/**
 * Hook to add test cases to a suite
 */
export function useAddTestCasesToSuite() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AddTestCasesToSuiteParams }) =>
      client.testSuites.addTestCases(id, data),
    onSuccess: (data, { id }) => {
      queryClient.setQueryData(testSuitesKeys.detail(id), data)
    },
  })
}

/**
 * Hook to remove a test case from a suite
 */
export function useRemoveTestCaseFromSuite() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ suiteId, testCaseId }: { suiteId: string; testCaseId: string }) =>
      client.testSuites.removeTestCase(suiteId, testCaseId),
    onSuccess: (_, { suiteId }) => {
      queryClient.invalidateQueries({ queryKey: testSuitesKeys.detail(suiteId) })
    },
  })
}

/**
 * Hook to reorder test cases in a suite
 */
export function useReorderTestCases() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ReorderTestCasesParams }) =>
      client.testSuites.reorderTestCases(id, data),
    onSuccess: (data, { id }) => {
      queryClient.setQueryData(testSuitesKeys.detail(id), data)
    },
  })
}

/**
 * Hook to manually run a test suite
 */
export function useRunTestSuite() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => client.testSuites.run(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: testSuitesKeys.runs(id) })
      queryClient.invalidateQueries({ queryKey: testSuitesKeys.detail(id) })
    },
  })
}

/**
 * Hook to fetch test suite runs
 */
export function useTestSuiteRuns(id: string, params?: TestSuiteRunsListParams, options?: { enabled?: boolean }) {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: testSuitesKeys.runsList(id, params),
    queryFn: () => client.testSuites.getRuns(id, params),
    enabled: options?.enabled !== false && !!id,
  })
}

/**
 * Hook to fetch a single test suite run
 */
export function useTestSuiteRun(suiteId: string, runId: string, options?: { enabled?: boolean }) {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: testSuitesKeys.run(suiteId, runId),
    queryFn: () => client.testSuites.getRun(suiteId, runId),
    enabled: options?.enabled !== false && !!suiteId && !!runId,
  })
}

/**
 * Hook to fetch flaky tests
 */
export function useFlakyTests() {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: testSuitesKeys.flaky(),
    queryFn: () => client.testSuites.flaky(),
  })
}

/**
 * Hook to fetch coverage overview
 */
export function useCoverageOverview() {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: testSuitesKeys.coverage(),
    queryFn: () => client.testSuites.coverage(),
  })
}
